/**
 * Knowledge-graph entity data for each council with a Fixtyer report page.
 *
 * Used by the council page JSON-LD (ServiceSchema / Report schema) to ground
 * each council as a disambiguated GovernmentOrganization entity for search
 * engines and LLM crawlers:
 *   - `name`      — official organisation name (Wikidata label)
 *   - `areaName`  — the administrative area the council maintains
 *   - `website`   — official council website (Wikidata P856 "official website")
 *   - `wikipedia` — canonical English Wikipedia article
 *   - `wikidata`  — Wikidata entity URL (anchors Google's Knowledge Graph)
 *
 * All URLs were validated against the live Wikipedia/Wikidata APIs on
 * 2026-06-12 (article resolved with redirects, Q-ID from page props, official
 * website from the Wikidata claim). When adding a new council, verify the
 * entity the same way — do not guess Wikidata IDs.
 */
export const DFT_ENTITY = {
    name: 'Department for Transport',
    url: 'https://www.gov.uk/government/organisations/department-for-transport',
    wikipedia: 'https://en.wikipedia.org/wiki/Department_for_Transport',
    wikidata: 'https://www.wikidata.org/wiki/Q2982287',
};
export const councilEntities = {
    'barking-and-dagenham': {
        name: 'Barking and Dagenham London Borough Council',
        areaName: 'Barking and Dagenham',
        website: 'https://www.lbbd.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Barking_and_Dagenham_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q4861056',
    },
    barnet: {
        name: 'Barnet London Borough Council',
        areaName: 'Barnet',
        website: 'https://www.barnet.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Barnet_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q4861611',
    },
    barnsley: {
        name: 'Barnsley Metropolitan Borough Council',
        areaName: 'Barnsley',
        website: 'https://www.barnsley.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Barnsley_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16950416',
    },
    'bath-and-north-east-somerset': {
        name: 'Bath and North East Somerset Council',
        areaName: 'Bath and North East Somerset',
        website: 'https://www.bathnes.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bath_and_North_East_Somerset_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16966588',
    },
    bedford: {
        name: 'Bedford Borough Council',
        areaName: 'Bedford',
        website: 'https://www.bedford.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bedford_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16239012',
    },
    bexley: {
        name: 'Bexley London Borough Council',
        areaName: 'Bexley',
        website: 'https://www.bexley.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bexley_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q4899799',
    },
    birmingham: {
        name: 'Birmingham City Council',
        areaName: 'Birmingham',
        website: 'https://www.birmingham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Birmingham_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q4916650',
    },
    'blackburn-with-darwen': {
        name: 'Blackburn with Darwen Borough Council',
        areaName: 'Blackburn with Darwen',
        website: 'https://www.blackburn.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Blackburn_with_Darwen_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16984481',
    },
    blackpool: {
        name: 'Blackpool Borough Council',
        areaName: 'Blackpool',
        website: 'https://www.blackpool.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Blackpool_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16984489',
    },
    bolton: {
        name: 'Bolton Metropolitan Borough Council',
        areaName: 'Bolton',
        website: 'https://www.bolton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bolton_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16952682',
    },
    'bournemouth-christchurch-and-poole': {
        name: 'Bournemouth, Christchurch and Poole Council',
        areaName: 'Bournemouth, Christchurch and Poole',
        website: 'https://www.bcpcouncil.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bournemouth,_Christchurch_and_Poole_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q54818205',
    },
    'bracknell-forest': {
        name: 'Bracknell Forest Council',
        areaName: 'Bracknell Forest',
        website: 'https://www.bracknell-forest.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bracknell_Forest_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16985082',
    },
    bradford: {
        name: 'City of Bradford Metropolitan District Council',
        areaName: 'Bradford',
        website: 'https://www.bradford.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/City_of_Bradford_Metropolitan_District_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16953331',
    },
    brent: {
        name: 'Brent London Borough Council',
        areaName: 'Brent',
        website: 'https://www.brent.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Brent_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q4961461',
    },
    'brighton-and-hove': {
        name: 'Brighton and Hove City Council',
        areaName: 'Brighton and Hove',
        website: 'https://www.brighton-hove.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Brighton_and_Hove_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16953725',
    },
    bristol: {
        name: 'Bristol City Council',
        areaName: 'Bristol',
        website: 'https://www.bristol.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bristol_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16953796',
    },
    bromley: {
        name: 'Bromley London Borough Council',
        areaName: 'Bromley',
        website: 'https://www.bromley.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bromley_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q4973687',
    },
    buckinghamshire: {
        name: 'Buckinghamshire Council',
        areaName: 'Buckinghamshire',
        website: 'https://www.buckinghamshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Buckinghamshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q65052846',
    },
    bury: {
        name: 'Bury Metropolitan Borough Council',
        areaName: 'Bury',
        website: 'https://www.bury.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Bury_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16954633',
    },
    calderdale: {
        name: 'Calderdale Metropolitan Borough Council',
        areaName: 'Calderdale',
        website: 'https://www.calderdale.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Calderdale_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16985873',
    },
    camden: {
        name: 'Camden London Borough Council',
        areaName: 'Camden',
        website: 'https://www.camden.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Camden_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5025790',
    },
    croydon: {
        name: 'Croydon London Borough Council',
        areaName: 'Croydon',
        website: 'https://www.croydon.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Croydon_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5189676',
    },
    ealing: {
        name: 'Ealing London Borough Council',
        areaName: 'Ealing',
        website: 'https://www.ealing.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Ealing_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5325486',
    },
    enfield: {
        name: 'Enfield London Borough Council',
        areaName: 'Enfield',
        website: 'https://www.enfield.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Enfield_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5377310',
    },
    hackney: {
        name: 'Hackney London Borough Council',
        areaName: 'Hackney',
        website: 'https://www.hackney.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hackney_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5637369',
    },
    'hammersmith-and-fulham': {
        name: 'Hammersmith and Fulham London Borough Council',
        areaName: 'Hammersmith and Fulham',
        website: 'https://www.lbhf.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hammersmith_and_Fulham_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5645758',
    },
    haringey: {
        name: 'Haringey London Borough Council',
        areaName: 'Haringey',
        website: 'https://www.haringey.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Haringey_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5657624',
    },
    harrow: {
        name: 'Harrow London Borough Council',
        areaName: 'Harrow',
        website: 'https://www.harrow.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Harrow_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5666565',
    },
    havering: {
        name: 'Havering London Borough Council',
        areaName: 'Havering',
        website: 'https://www.havering.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Havering_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5683826',
    },
    hillingdon: {
        name: 'Hillingdon London Borough Council',
        areaName: 'Hillingdon',
        website: 'https://www.hillingdon.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hillingdon_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5763303',
    },
    hounslow: {
        name: 'Hounslow London Borough Council',
        areaName: 'Hounslow',
        website: 'https://www.hounslow.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hounslow_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5913301',
    },
    islington: {
        name: 'Islington London Borough Council',
        areaName: 'Islington',
        website: 'https://www.islington.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Islington_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6084379',
    },
    lambeth: {
        name: 'Lambeth London Borough Council',
        areaName: 'Lambeth',
        website: 'https://www.lambeth.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Lambeth_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6481450',
    },
    lewisham: {
        name: 'Lewisham London Borough Council',
        areaName: 'Lewisham',
        website: 'https://www.lewisham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Lewisham_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6537320',
    },
    merton: {
        name: 'Merton London Borough Council',
        areaName: 'Merton',
        website: 'https://www.merton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Merton_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6820723',
    },
    newham: {
        name: 'Newham London Borough Council',
        areaName: 'Newham',
        website: 'https://www.newham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Newham_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7018141',
    },
    redbridge: {
        name: 'Redbridge London Borough Council',
        areaName: 'Redbridge',
        website: 'https://www.redbridge.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Redbridge_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7305508',
    },
    'richmond-upon-thames': {
        name: 'Richmond upon Thames London Borough Council',
        areaName: 'Richmond upon Thames',
        website: 'https://www.richmond.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Richmond_upon_Thames_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7331065',
    },
    southwark: {
        name: 'Southwark London Borough Council',
        areaName: 'Southwark',
        website: 'https://www.southwark.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Southwark_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7571153',
    },
    sutton: {
        name: 'Sutton London Borough Council',
        areaName: 'Sutton',
        website: 'https://www.sutton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Sutton_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7650288',
    },
    cornwall: {
        name: 'Cornwall Council',
        areaName: 'Cornwall',
        website: 'https://www.cornwall.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Cornwall_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q3774189',
    },
    coventry: {
        name: 'Coventry City Council',
        areaName: 'Coventry',
        website: 'https://www.coventry.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Coventry_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5179058',
    },
    cambridgeshire: {
        name: 'Cambridgeshire County Council',
        areaName: 'Cambridgeshire',
        website: 'https://www.cambridgeshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Cambridgeshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5025648',
    },
    'central-bedfordshire': {
        name: 'Central Bedfordshire Council',
        areaName: 'Central Bedfordshire',
        website: 'https://www.centralbedfordshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Central_Bedfordshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16986562',
    },
    'cheshire-east': {
        name: 'Cheshire East Council',
        areaName: 'Cheshire East',
        website: 'https://www.cheshireeast.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Cheshire_East_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16829355',
    },
    'cheshire-west-and-chester': {
        name: 'Cheshire West and Chester Council',
        areaName: 'Cheshire West and Chester',
        website: 'https://www.cheshirewestandchester.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Cheshire_West_and_Chester_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16829360',
    },
    cumberland: {
        name: 'Cumberland Council',
        areaName: 'Cumberland',
        website: 'https://www.cumberland.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Cumberland_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q112160997',
    },
    derbyshire: {
        name: 'Derbyshire County Council',
        areaName: 'Derbyshire',
        website: 'https://www.derbyshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Derbyshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5261561',
    },
    doncaster: {
        name: 'Doncaster Metropolitan Borough Council',
        areaName: 'Doncaster',
        website: 'https://www.doncaster.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/City_of_Doncaster_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16988947',
    },
    devon: {
        name: 'Devon County Council',
        areaName: 'Devon',
        website: 'https://www.devon.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Devon_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6385990',
    },
    dorset: {
        name: 'Dorset Council',
        areaName: 'Dorset',
        website: 'https://www.dorsetcouncil.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Dorset_Council_(UK)',
        wikidata: 'https://www.wikidata.org/wiki/Q55609905',
    },
    dudley: {
        name: 'Dudley Metropolitan Borough Council',
        areaName: 'Dudley',
        website: 'https://www.dudley.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Dudley_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16991635',
    },
    durham: {
        name: 'Durham County Council',
        areaName: 'County Durham',
        website: 'https://www.durham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Durham_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5316492',
    },
    'east-riding-of-yorkshire': {
        name: 'East Riding of Yorkshire Council',
        areaName: 'East Riding of Yorkshire',
        website: 'https://www.eastriding.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/East_Riding_of_Yorkshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16989282',
    },
    'east-sussex': {
        name: 'East Sussex County Council',
        areaName: 'East Sussex',
        website: 'https://www.eastsussex.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/East_Sussex_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5329458',
    },
    essex: {
        name: 'Essex County Council',
        areaName: 'Essex',
        website: 'https://www.essex.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Essex_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5399679',
    },
    gloucestershire: {
        name: 'Gloucestershire County Council',
        areaName: 'Gloucestershire',
        website: 'https://www.gloucestershire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Gloucestershire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16839354',
    },
    greenwich: {
        name: 'Greenwich London Borough Council',
        areaName: 'Greenwich',
        website: 'https://www.royalgreenwich.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Greenwich_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5604860',
    },
    hampshire: {
        name: 'Hampshire County Council',
        areaName: 'Hampshire',
        website: 'https://www.hants.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hampshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5646045',
    },
    hertfordshire: {
        name: 'Hertfordshire County Council',
        areaName: 'Hertfordshire',
        website: 'https://www.hertfordshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hertfordshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5744765',
    },
    'kensington-and-chelsea': {
        name: 'Kensington and Chelsea London Borough Council',
        areaName: 'Kensington and Chelsea',
        website: 'https://www.rbkc.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Kensington_and_Chelsea_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6391507',
    },
    // Gateshead's council has no standalone Wikipedia article — the borough
    // article is the canonical anchor; the Wikidata ID is the council org itself.
    gateshead: {
        name: 'Gateshead Metropolitan Borough Council',
        areaName: 'Gateshead',
        website: 'https://www.gateshead.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Metropolitan_Borough_of_Gateshead',
        wikidata: 'https://www.wikidata.org/wiki/Q54666929',
    },
    kent: {
        name: 'Kent County Council',
        areaName: 'Kent',
        website: 'https://www.kent.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Kent_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386127',
    },
    lancashire: {
        name: 'Lancashire County Council',
        areaName: 'Lancashire',
        website: 'https://www.lancashire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Lancashire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386149',
    },
    leeds: {
        name: 'Leeds City Council',
        areaName: 'Leeds',
        website: 'https://www.leeds.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Leeds_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6515870',
    },
    leicester: {
        name: 'Leicester City Council',
        areaName: 'Leicester',
        website: 'https://www.leicester.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Leicester_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6519163',
    },
    leicestershire: {
        name: 'Leicestershire County Council',
        areaName: 'Leicestershire',
        website: 'https://www.leicestershire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Leicestershire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386198',
    },
    lincolnshire: {
        name: 'Lincolnshire County Council',
        areaName: 'Lincolnshire',
        website: 'https://www.lincolnshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Lincolnshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16996347',
    },
    liverpool: {
        name: 'Liverpool City Council',
        areaName: 'Liverpool',
        website: 'https://www.liverpool.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Liverpool_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6658351',
    },
    kirklees: {
        name: 'Kirklees Metropolitan Borough Council',
        areaName: 'Kirklees',
        website: 'https://www.kirklees.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Kirklees_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16995709',
    },
    knowsley: {
        name: 'Knowsley Metropolitan Borough Council',
        areaName: 'Knowsley',
        website: 'https://www.knowsley.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Knowsley_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16995750',
    },
    manchester: {
        name: 'Manchester City Council',
        areaName: 'Manchester',
        website: 'https://www.manchester.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Manchester_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q2302252',
    },
    norfolk: {
        name: 'Norfolk County Council',
        areaName: 'Norfolk',
        website: 'https://www.norfolk.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Norfolk_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386227',
    },
    'north-lincolnshire': {
        name: 'North Lincolnshire Council',
        areaName: 'North Lincolnshire',
        website: 'https://www.northlincs.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/North_Lincolnshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17016863',
    },
    'north-northamptonshire': {
        name: 'North Northamptonshire Council',
        areaName: 'North Northamptonshire',
        website: 'https://www.northnorthants.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/North_Northamptonshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q111232446',
    },
    'north-yorkshire': {
        name: 'North Yorkshire Council',
        areaName: 'North Yorkshire',
        website: 'https://www.northyorks.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/North_Yorkshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17017103',
    },
    northumberland: {
        name: 'Northumberland County Council',
        areaName: 'Northumberland',
        website: 'https://www.northumberland.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Northumberland_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386521',
    },
    nottingham: {
        name: 'Nottingham City Council',
        areaName: 'Nottingham',
        website: 'https://www.nottinghamcity.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Nottingham_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7063612',
    },
    newcastle: {
        name: 'Newcastle City Council',
        areaName: 'Newcastle upon Tyne',
        website: 'https://www.newcastle.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Newcastle_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16998823',
    },
    nottinghamshire: {
        name: 'Nottinghamshire County Council',
        areaName: 'Nottinghamshire',
        website: 'https://www.nottinghamshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Nottinghamshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16934215',
    },
    oldham: {
        name: 'Oldham Metropolitan Borough Council',
        areaName: 'Oldham',
        website: 'https://www.oldham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Oldham_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17017730',
    },
    oxfordshire: {
        name: 'Oxfordshire County Council',
        areaName: 'Oxfordshire',
        website: 'https://www.oxfordshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Oxfordshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386253',
    },
    rochdale: {
        name: 'Rochdale Metropolitan Borough Council',
        areaName: 'Rochdale',
        website: 'https://www.rochdale.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Rochdale_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17019804',
    },
    rotherham: {
        name: 'Rotherham Metropolitan Borough Council',
        areaName: 'Rotherham',
        website: 'https://www.rotherham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Rotherham_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17020059',
    },
    salford: {
        name: 'Salford City Council',
        areaName: 'Salford',
        website: 'https://www.salford.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Salford_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17020402',
    },
    sefton: {
        name: 'Sefton Metropolitan Borough Council',
        areaName: 'Sefton',
        website: 'https://www.sefton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Sefton_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7446119',
    },
    sheffield: {
        name: 'Sheffield City Council',
        areaName: 'Sheffield',
        website: 'https://www.sheffield.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Sheffield_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7492609',
    },
    shropshire: {
        name: 'Shropshire Council',
        areaName: 'Shropshire',
        website: 'https://www.shropshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Shropshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q93612',
    },
    slough: {
        name: 'Slough Borough Council',
        areaName: 'Slough',
        website: 'https://www.slough.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Slough_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7541466',
    },
    somerset: {
        name: 'Somerset Council',
        areaName: 'Somerset',
        website: 'https://www.somerset.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Somerset_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386270',
    },
    'st-helens': {
        name: 'St Helens Metropolitan Borough Council',
        areaName: 'St Helens',
        website: 'https://www.sthelens.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/St_Helens_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17022094',
    },
    staffordshire: {
        name: 'Staffordshire County Council',
        areaName: 'Staffordshire',
        website: 'https://www.staffordshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Staffordshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386295',
    },
    stockport: {
        name: 'Stockport Metropolitan Borough Council',
        areaName: 'Stockport',
        website: 'https://www.stockport.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Stockport_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7618164',
    },
    suffolk: {
        name: 'Suffolk County Council',
        areaName: 'Suffolk',
        website: 'https://www.suffolk.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Suffolk_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q3305205',
    },
    sunderland: {
        name: 'Sunderland City Council',
        areaName: 'Sunderland',
        website: 'https://www.sunderland.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Sunderland_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17022675',
    },
    surrey: {
        name: 'Surrey County Council',
        areaName: 'Surrey',
        website: 'https://www.surreycc.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Surrey_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386325',
    },
    warwickshire: {
        name: 'Warwickshire County Council',
        areaName: 'Warwickshire',
        website: 'https://www.warwickshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Warwickshire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17066348',
    },
    tameside: {
        name: 'Tameside Metropolitan Borough Council',
        areaName: 'Tameside',
        website: 'https://www.tameside.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Tameside_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17041259',
    },
    trafford: {
        name: 'Trafford Metropolitan Borough Council',
        areaName: 'Trafford',
        website: 'https://www.trafford.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Trafford_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17034839',
    },
    wakefield: {
        name: 'Wakefield Metropolitan District Council',
        areaName: 'Wakefield',
        website: 'https://www.wakefield.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Wakefield_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7961061',
    },
    'waltham-forest': {
        name: 'Waltham Forest London Borough Council',
        areaName: 'Waltham Forest',
        website: 'https://www.walthamforest.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Waltham_Forest_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7966606',
    },
    'west-northamptonshire': {
        name: 'West Northamptonshire Council',
        areaName: 'West Northamptonshire',
        website: 'https://www.westnorthants.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/West_Northamptonshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q111232469',
    },
    'west-sussex': {
        name: 'West Sussex County Council',
        areaName: 'West Sussex',
        website: 'https://www.westsussex.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/West_Sussex_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6386353',
    },
    'westmorland-and-furness': {
        name: 'Westmorland and Furness Council',
        areaName: 'Westmorland and Furness',
        website: 'https://www.westmorlandandfurness.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Westmorland_and_Furness_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q111962803',
    },
    wigan: {
        name: 'Wigan Metropolitan Borough Council',
        areaName: 'Wigan',
        website: 'https://www.wigan.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Wigan_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7999507',
    },
    wiltshire: {
        name: 'Wiltshire Council',
        areaName: 'Wiltshire',
        website: 'https://wiltshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Wiltshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q1990879',
    },
    wirral: {
        name: 'Wirral Metropolitan Borough Council',
        areaName: 'Wirral',
        website: 'https://www.wirral.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Wirral_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17038877',
    },
    wolverhampton: {
        name: 'City of Wolverhampton Council',
        areaName: 'Wolverhampton',
        website: 'https://www.wolverhampton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/City_of_Wolverhampton_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q8030462',
    },
    worcestershire: {
        name: 'Worcestershire County Council',
        areaName: 'Worcestershire',
        website: 'https://www.worcestershire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Worcestershire_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q8034245',
    },
    'city-of-london': {
        name: 'City of London Corporation',
        areaName: 'City of London',
        website: 'https://www.cityoflondon.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/City_of_London_Corporation',
        wikidata: 'https://www.wikidata.org/wiki/Q1094147',
    },
    // Darlington's council has no standalone Wikipedia article — the borough
    // article is the canonical anchor; the Wikidata ID is the council org itself.
    darlington: {
        name: 'Darlington Borough Council',
        areaName: 'Darlington',
        website: 'https://www.darlington.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Borough_of_Darlington',
        wikidata: 'https://www.wikidata.org/wiki/Q16948698',
    },
    derby: {
        name: 'Derby City Council',
        areaName: 'Derby',
        website: 'https://www.derby.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Derby_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5261430',
    },
    halton: {
        name: 'Halton Borough Council',
        areaName: 'Halton',
        website: 'https://www3.halton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Halton_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16983144',
    },
    // Hartlepool's council has no standalone Wikipedia article — the borough
    // article is the canonical anchor; the Wikidata ID is the council org itself.
    hartlepool: {
        name: 'Hartlepool Borough Council',
        areaName: 'Hartlepool',
        website: 'https://www.hartlepool.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Borough_of_Hartlepool',
        wikidata: 'https://www.wikidata.org/wiki/Q16993720',
    },
    herefordshire: {
        name: 'Herefordshire Council',
        areaName: 'Herefordshire',
        website: 'https://www.herefordshire.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Herefordshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5738061',
    },
    hull: {
        name: 'Hull City Council',
        areaName: 'Kingston upon Hull',
        website: 'https://www.hull.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Hull_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q5935834',
    },
    'isle-of-wight': {
        name: 'Isle of Wight Council',
        areaName: 'Isle of Wight',
        website: 'https://www.iow.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Isle_of_Wight_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6084095',
    },
    'kingston-upon-thames': {
        name: 'Kingston upon Thames London Borough Council',
        areaName: 'Kingston upon Thames',
        website: 'https://www.kingston.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Kingston_upon_Thames_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6413671',
    },
    luton: {
        name: 'Luton Borough Council',
        areaName: 'Luton',
        website: 'https://www.luton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Luton_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16997109',
    },
    medway: {
        name: 'Medway Council',
        areaName: 'Medway',
        website: 'https://www.medway.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Medway_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16997526',
    },
    middlesbrough: {
        name: 'Middlesbrough Council',
        areaName: 'Middlesbrough',
        website: 'https://www.middlesbrough.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Middlesbrough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6841843',
    },
    'north-tyneside': {
        name: 'North Tyneside Council',
        areaName: 'North Tyneside',
        website: 'https://www.northtyneside.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/North_Tyneside_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17017005',
    },
    sandwell: {
        name: 'Sandwell Metropolitan Borough Council',
        areaName: 'Sandwell',
        website: 'https://www.sandwell.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Sandwell_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7417057',
    },
    solihull: {
        name: 'Solihull Metropolitan Borough Council',
        areaName: 'Solihull',
        website: 'https://www.solihull.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Solihull_Metropolitan_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17021590',
    },
    'south-tyneside': {
        name: 'South Tyneside Council',
        areaName: 'South Tyneside',
        website: 'https://southtyneside.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/South_Tyneside_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q23463529',
    },
    'tower-hamlets': {
        name: 'Tower Hamlets London Borough Council',
        areaName: 'Tower Hamlets',
        website: 'https://www.towerhamlets.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Tower_Hamlets_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7829677',
    },
    walsall: {
        name: 'Walsall Metropolitan Borough Council',
        areaName: 'Walsall',
        website: 'https://go.walsall.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Walsall_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7963789',
    },
    wandsworth: {
        name: 'Wandsworth London Borough Council',
        areaName: 'Wandsworth',
        website: 'https://www.wandsworth.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Wandsworth_London_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7967311',
    },
    westminster: {
        name: 'Westminster City Council',
        areaName: 'Westminster',
        website: 'https://www.westminster.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Westminster_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7989145',
    },
    'isles-of-scilly': {
        name: 'Council of the Isles of Scilly',
        areaName: 'Isles of Scilly',
        website: 'https://www.scilly.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Council_of_the_Isles_of_Scilly',
        wikidata: 'https://www.wikidata.org/wiki/Q28195166',
    },
    'milton-keynes': {
        name: 'Milton Keynes City Council',
        areaName: 'Milton Keynes',
        website: 'https://www.milton-keynes.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Milton_Keynes_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q16997843',
    },
    'north-east-lincolnshire': {
        name: 'North East Lincolnshire Council',
        areaName: 'North East Lincolnshire',
        website: 'https://www.nelincs.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/North_East_Lincolnshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17016820',
    },
    'north-somerset': {
        name: 'North Somerset Council',
        areaName: 'North Somerset',
        website: 'https://n-somerset.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/North_Somerset_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17016922',
    },
    peterborough: {
        name: 'Peterborough City Council',
        areaName: 'Peterborough',
        website: 'https://www.peterborough.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Peterborough_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q6664413',
    },
    plymouth: {
        name: 'Plymouth City Council',
        areaName: 'Plymouth',
        website: 'https://www.plymouth.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Plymouth_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7205775',
    },
    portsmouth: {
        name: 'Portsmouth City Council',
        areaName: 'Portsmouth',
        website: 'https://www.portsmouth.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Portsmouth_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17104656',
    },
    reading: {
        name: 'Reading Borough Council',
        areaName: 'Reading',
        website: 'https://www.reading.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Reading_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17018654',
    },
    'redcar-and-cleveland': {
        name: 'Redcar and Cleveland Borough Council',
        areaName: 'Redcar and Cleveland',
        website: 'https://www.redcar-cleveland.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Redcar_and_Cleveland_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17019415',
    },
    rutland: {
        name: 'Rutland County Council',
        areaName: 'Rutland',
        website: 'https://www.rutland.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Rutland_County_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7383481',
    },
    'south-gloucestershire': {
        name: 'South Gloucestershire Council',
        areaName: 'South Gloucestershire',
        website: 'https://www.southglos.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/South_Gloucestershire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17020724',
    },
    southampton: {
        name: 'Southampton City Council',
        areaName: 'Southampton',
        website: 'https://www.southampton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Southampton_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17021809',
    },
    'southend-on-sea': {
        name: 'Southend-on-Sea City Council',
        areaName: 'Southend-on-Sea',
        website: 'https://www.southend.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Southend-on-Sea_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q7569516',
    },
    'stockton-on-tees': {
        name: 'Stockton-on-Tees Borough Council',
        areaName: 'Stockton-on-Tees',
        website: 'https://www.stockton.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Stockton-on-Tees_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17022493',
    },
    'stoke-on-trent': {
        name: 'Stoke-on-Trent City Council',
        areaName: 'Stoke-on-Trent',
        website: 'https://www.stoke.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Stoke-on-Trent_City_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17022507',
    },
    swindon: {
        name: 'Swindon Borough Council',
        areaName: 'Swindon',
        website: 'https://www.swindon.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Swindon_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17040033',
    },
    'telford-and-wrekin': {
        name: 'Telford and Wrekin Council',
        areaName: 'Telford and Wrekin',
        website: 'https://www.telford.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Telford_and_Wrekin_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17059334',
    },
    thurrock: {
        name: 'Thurrock Council',
        areaName: 'Thurrock',
        website: 'https://www.thurrock.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Thurrock_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q1646552',
    },
    torbay: {
        name: 'Torbay Council',
        areaName: 'Torbay',
        website: 'https://www.torbay.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Torbay_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17034697',
    },
    warrington: {
        name: 'Warrington Borough Council',
        areaName: 'Warrington',
        website: 'https://www.warrington.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Warrington_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17033474',
    },
    'west-berkshire': {
        name: 'West Berkshire Council',
        areaName: 'West Berkshire',
        website: 'https://www.westberks.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/West_Berkshire_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17041496',
    },
    'windsor-and-maidenhead': {
        name: 'Windsor and Maidenhead Borough Council',
        areaName: 'Windsor and Maidenhead',
        website: 'https://www.rbwm.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Windsor_and_Maidenhead_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17038560',
    },
    wokingham: {
        name: 'Wokingham Borough Council',
        areaName: 'Wokingham',
        website: 'https://www.wokingham.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/Wokingham_Borough_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17039435',
    },
    york: {
        name: 'City of York Council',
        areaName: 'York',
        website: 'https://www.york.gov.uk',
        wikipedia: 'https://en.wikipedia.org/wiki/City_of_York_Council',
        wikidata: 'https://www.wikidata.org/wiki/Q17059453',
    },
};
